import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { PageLoader } from '@/design-system/components/ui';
import { canAccessPortal, type PortalKey } from '@/features/access/portals';
import { accessForPortal, usePortalAccess, usePortalScope } from '@/features/access/preview';
import { hasPermission, type PermissionCode } from '@/features/access/types';
import { BackendNotice } from '@/components/BackendNotice';
import { useAuth } from './context';

/**
 * Route-level authorization. This is the second of three layers:
 *   1. UI (navigation hidden)   2. route guards (here)   3. database RLS (authoritative)
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, access } = useAuth();
  const location = useLocation();
  if (status === 'unconfigured') return <BackendNotice />;
  if (status === 'loading') return <PageLoader />;
  if (status === 'signed_out') {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  // An administrator required a new password: nothing else opens until it is set.
  if (access?.passwordResetRequired) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth/reset-password?required=1&next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

/** Portal entry: the user's role or portal permission (the previewed role's, intersected with the user's own, while previewing). */
export function RequirePortal({ portal, children }: { portal: PortalKey; children: ReactNode }) {
  const { access } = accessForPortal(usePortalAccess(), portal);
  return (
    <RequireAuth>{canAccessPortal(access, portal) ? children : <Navigate to="/forbidden" replace />}</RequireAuth>
  );
}

/** Screen-level check inside a portal, with the access of the portal being shown. */
export function RequirePermission({ permission, children }: { permission: PermissionCode; children: ReactNode }) {
  const { access } = usePortalScope();
  return hasPermission(access, permission) ? <>{children}</> : <Navigate to="/forbidden" replace />;
}

/** Sends already-signed-in users away from login/register pages. */
export function RedirectIfSignedIn({ to, children }: { to: string; children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <PageLoader />;
  if (status === 'signed_in') return <Navigate to={to} replace />;
  return <>{children}</>;
}
